/**
 * Content model - Helper functions for parsing, serializing, and validating step content (coach_content JSON)
 *
 * Content is stored as a JSON string containing an array of blocks.
 * Each block has:
 * - type: "open_prompt" (required)
 * - goals: array of {name, description}; an author gives each sub-step at least one
 * Stored content may still carry `memory_items` on a block; serializing drops them, as the server does.
 */

/**
 * Parse content JSON string into blocks array
 * @param {string} jsonString - The JSON string to parse
 * @returns {Array} - Array of blocks, or empty array if parsing fails
 */
export function parseCoachContent(jsonString) {
  if (!jsonString || jsonString.trim() === '' || jsonString === '[]') {
    return [];
  }
  
  try {
    const parsed = JSON.parse(jsonString);
    if (!Array.isArray(parsed)) {
      console.error('Content must be an array');
      return [];
    }
    return parsed;
  } catch (error) {
    console.error('Failed to parse content:', error);
    return [];
  }
}

/**
 * True when coach blocks include at least one goal with non-whitespace text.
 * @param {Array} blocks - Parsed coach blocks (see {@link parseCoachContent})
 * @returns {boolean}
 */
export function coachBlocksHaveContent(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) return false;
  return blocks.some((block) => {
    const goals = block?.goals || [];
    return goals.some((g) => String(g?.name || "").trim() || String(g?.description || "").trim());
  });
}

/**
 * Strip fields the API does not allow on open_prompt blocks (e.g. bot_lesson_guidelines from LLM output).
 * Server schema: only type and goals — see server/main/validators/coach_content_validator.py
 */
function sanitizeGoal(goal) {
  if (!goal || typeof goal !== "object") {
    return { name: "", description: "" };
  }
  return {
    name: typeof goal.name === "string" ? goal.name : "",
    description: typeof goal.description === "string" ? goal.description : "",
  };
}

function sanitizeOpenPromptBlock(block) {
  if (!block || typeof block !== "object") {
    return { type: "open_prompt", goals: [] };
  }
  const goals = Array.isArray(block.goals) ? block.goals.map(sanitizeGoal) : [];
  return {
    type: "open_prompt",
    goals,
  };
}

/**
 * Serialize blocks array to JSON string
 * @param {Array} blocks - Array of blocks
 * @returns {string} - Minified JSON string
 */
export function serializeCoachContent(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return "[]";
  }

  try {
    const sanitized = blocks.map(sanitizeOpenPromptBlock);
    return JSON.stringify(sanitized, null, 0);
  } catch (error) {
    console.error("Failed to serialize content:", error);
    return "[]";
  }
}

/**
 * Create a new empty block with default structure
 * @returns {Object} - New block object
 */
export function createEmptyBlock() {
  return {
    type: 'open_prompt',
    goals: []
  };
}

/**
 * New goal row from "Add a goal" — empty fields; user edits in place (autosave may fail validation until filled).
 */
export function createEmptyGoal() {
  return {
    name: "",
    description: "",
  };
}

/**
 * Default sub-step shape for generators / schema fallbacks that must pass server validation immediately.
 * (User-added goals use createEmptyGoal() and are filled in the editor.)
 */
export function createDefaultSubstepBlock() {
  return {
    type: "open_prompt",
    goals: [{ name: "New goal", description: "Add a description" }],
  };
}

/**
 * Validate a single block
 * @param {Object} block - Block to validate
 * @param {number} blockIndex - Index of the block (for error messages)
 * @returns {Array} - Array of error messages
 */
export function validateBlock(block, blockIndex) {
  const errors = [];
  const subStepNumber = (blockIndex ?? 0) + 1;

  if (!block || typeof block !== "object") {
    errors.push(`Sub-step ${subStepNumber}: Must be an object`);
    return errors;
  }

  // Check type field
  if (!block.type) {
    errors.push(`Sub-step ${subStepNumber}: Missing required field 'type'`);
  } else if (block.type !== "open_prompt") {
    errors.push(`Sub-step ${subStepNumber}: Type must be 'open_prompt'`);
  }

  // The server keeps a sub-step with no goal, which closes on its first turn; an author gives each sub-step a goal.
  if (!Array.isArray(block.goals) || block.goals.length === 0) {
    errors.push(`Sub-step ${subStepNumber} must contain at least one goal`);
  }

  // Validate goals
  if (Array.isArray(block.goals)) {
    block.goals.forEach((goal, idx) => {
      if (!goal.name || goal.name.trim() === "") {
        errors.push(`Sub-step ${subStepNumber}, Goal ${idx + 1}: 'name' is required and cannot be empty`);
      }
      if (!goal.description || goal.description.trim() === "") {
        errors.push(`Sub-step ${subStepNumber}, Goal ${idx + 1}: 'description' is required and cannot be empty`);
      }
    });
  }

  return errors;
}

/**
 * Validate entire content (all blocks)
 * @param {Array} blocks - Array of blocks
 * @param {boolean} isActive - Whether the lesson step is active
 * @returns {Object} - {valid: boolean, errors: Array}
 */
export function validateCoachContent(blocks, isActive = false) {
  // If not active, allow empty or invalid content
  if (!isActive) {
    return { valid: true, errors: [] };
  }
  
  const errors = [];
  
  // Must have at least one block when active
  if (!Array.isArray(blocks) || blocks.length === 0) {
    errors.push("At least one sub-step is required for active lesson steps");
    return { valid: false, errors };
  }
  
  // Validate each block
  blocks.forEach((block, idx) => {
    const blockErrors = validateBlock(block, idx);
    errors.push(...blockErrors);
  });
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get a display name for a block (for UI)
 * @param {Object} block - Block object
 * @param {number} index - Block index
 * @returns {string} - Display name
 */
export function getBlockDisplayName(block, index) {
  if (!block) return "Goals";

  const goalCount = Array.isArray(block.goals) ? block.goals.length : 0;
  if (goalCount === 0) {
    return "Goals (empty)";
  }

  return `Goals — ${goalCount} goal${goalCount !== 1 ? "s" : ""}`;
}
